import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useParams } from "react-router-dom";
import { formatDate } from "../ClientUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { StatusBadge } from "@/components/attendance/table/StatusBadge";
import { useUserStore } from "@/store/userStore";

const ITEMS_PER_PAGE = 10;

type Category =
  | "joker"
  | "late_consumed"
  | "admin_cancellation"
  | "on_time"
  | "late_no_audit"
  | null;

interface HistoryRow {
  id: string;
  class_date: string;
  time: string | null;
  status: string;
  class_name: string | null;
  hours_before: number | null;
  cancellation_category: Category;
}

interface Summary {
  completed: number;
  jokers: number;
  late_consumed: number;
  on_time: number;
  admin_cancellation: number;
  late_no_audit: number;
  total: number;
}

function CancellationBadge({ category, hoursBefore }: { category: Category; hoursBefore: number | null }) {
  const { t } = useTranslation(["adminui3"]);
  if (!category) return null;

  const config: Record<NonNullable<Category>, { label: string; className: string; tooltip: string }> = {
    joker: {
      label: t("history.jokerUsed", { ns: "adminui3" }),
      className: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30",
      tooltip: t("history.jokerUsedTooltip", { ns: "adminui3" }),
    },
    late_consumed: {
      label: t("history.lateConsumed", { ns: "adminui3" }),
      className: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
      tooltip: t("history.lateConsumedTooltip", { ns: "adminui3" }),
    },
    admin_cancellation: {
      label: t("history.adminCancellation", { ns: "adminui3" }),
      className: "bg-muted text-muted-foreground border-border",
      tooltip: t("history.adminCancellationTooltip", { ns: "adminui3" }),
    },
    on_time: {
      label: t("history.onTime", { ns: "adminui3" }),
      className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
      tooltip: t("history.onTimeTooltip", { ns: "adminui3" }),
    },
    late_no_audit: {
      label: t("history.lateNoAudit", { ns: "adminui3" }),
      className: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
      tooltip: t("history.lateNoAuditTooltip", { ns: "adminui3" }),
    },
  };

  const c = config[category];
  const hoursText =
    hoursBefore !== null
      ? hoursBefore >= 0
        ? t("history.hoursBefore", { ns: "adminui3", hours: hoursBefore.toFixed(1) })
        : t("history.hoursAfter", { ns: "adminui3", hours: Math.abs(hoursBefore).toFixed(1) })
      : "";

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge variant="outline" className={`text-xs cursor-help ${c.className}`}>
            {c.label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs text-xs">{c.tooltip}{hoursText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ClientHistoryTab() {
  const { t } = useTranslation(["common", "clients", "classes", "adminui3"]);
  const { id } = useParams();
  const gymId = useUserStore((s) => s.gymId);
  const [isLoading, setIsLoading] = useState(true);
  const [reservations, setReservations] = useState<HistoryRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = useIsMobile();

  useEffect(() => {
    async function fetchReservationHistory() {
      if (!id || !gymId) return;

      setIsLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("bookings-management", {
          body: {
            action: "get_client_booking_history_admin",
            gym_id: gymId,
            target_user_id: id,
          },
        });

        if (error) throw error;

        setReservations((data?.history || []) as HistoryRow[]);
        setSummary((data?.summary || null) as Summary | null);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error fetching client history:", err);
        setReservations([]);
        setSummary(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchReservationHistory();
  }, [id, gymId]);

  const totalPages = Math.ceil(reservations.length / ITEMS_PER_PAGE);
  const paginatedReservations = reservations.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center flex-wrap gap-2">
          <CardTitle>{t("clients:classHistory")}</CardTitle>
        </div>
        {summary && (
          <div className="flex flex-wrap gap-2 pt-3 text-xs">
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
              {t("history.completedCount", { ns: "adminui3", count: summary.completed, plural: summary.completed > 1 ? "s" : "" })}
            </Badge>
            <Badge variant="outline" className="bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30">
              {t("history.jokersCount", { ns: "adminui3", count: summary.jokers, plural: summary.jokers > 1 ? "s" : "" })}
            </Badge>
            <Badge variant="outline" className="bg-orange-500/10 text-orange-700 dark:text-orange-300 border-orange-500/30">
              {t("history.lateConsumedCount", { ns: "adminui3", count: summary.late_consumed, plural: summary.late_consumed > 1 ? "s" : "" })}
            </Badge>
            <Badge variant="outline" className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">
              {t("history.onTimeCount", { ns: "adminui3", count: summary.on_time })}
            </Badge>
            <Badge variant="outline" className="bg-muted text-muted-foreground">
              {t("history.adminCancellationCount", { ns: "adminui3", count: summary.admin_cancellation })}
            </Badge>
            {summary.late_no_audit > 0 && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30">
                {t("history.lateNoAuditCount", { ns: "adminui3", count: summary.late_no_audit, plural: summary.late_no_audit > 1 ? "s" : "" })}
              </Badge>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">{t("common:loading")}</p>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("classes:class")}</TableHead>
                  <TableHead>{t("common:date")}</TableHead>
                  <TableHead>{t("common:time")}</TableHead>
                  <TableHead>{t("common:status")}</TableHead>
                  <TableHead>{t("history.cancellationDetail", { ns: "adminui3" })}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedReservations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-4 text-muted-foreground">
                      {t("noClassesFound", { ns: "classes" })}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedReservations.map((reservation) => (
                    <TableRow key={reservation.id}>
                      <TableCell className="font-medium">{reservation.class_name || "—"}</TableCell>
                      <TableCell>{formatDate(reservation.class_date)}</TableCell>
                      <TableCell>{reservation.time || "—"}</TableCell>
                      <TableCell>
                        <StatusBadge status={reservation.status as "cancelled" | "completed" | "upcoming"} isMobile={isMobile} />
                      </TableCell>
                      <TableCell>
                        <CancellationBadge
                          category={reservation.cancellation_category}
                          hoursBefore={reservation.hours_before}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {reservations.length > ITEMS_PER_PAGE && (
              <div className="flex justify-end gap-4 mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  {t("common:previous")}
                </Button>
                <span className="text-sm text-muted-foreground self-center">
                  {t("common:page")} {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  {t("common:next")}
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
